/**
 * Calculator Integration Utilities
 * Handles data export/import between Budget, Breach Cost, and ROI calculators
 */

import {
  BudgetCalculation,
  BreachCostResult,
  ROSIResult,
  URLIntegrationParams,
  IndustrySector
} from '@/types/calculator';

// ============================================================================
// URL PARAMETER ENCODING/DECODING
// ============================================================================

/**
 * Encode calculator results into URL-safe parameters
 */
export function encodeCalculatorResults(
  source: 'budget' | 'breach_cost' | 'roi',
  data: BudgetCalculation | BreachCostResult | ROSIResult,
  additionalParams?: Record<string, string | number>
): URLIntegrationParams {
  const params: URLIntegrationParams = {
    source
  };

  // Add additional parameters if provided
  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      params[key] = String(value);
    });
  }

  // Source-specific encoding
  if (source === 'budget' && 'recommended' in data) {
    params.recommendedBudget = String(Math.round(data.recommended));
  } else if (source === 'breach_cost' && 'totalCost' in data) {
    params.breachCost = String(Math.round(data.totalCost));
  }

  return params;
}

/**
 * Decode URL parameters into calculator inputs
 */
export function decodeURLParams(searchParams: URLSearchParams): URLIntegrationParams {
  const params: URLIntegrationParams = {};

  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
}

/**
 * Build URL for calculator navigation with parameters
 */
export function buildCalculatorURL(
  targetCalculator: 'budget' | 'breach_cost' | 'roi',
  params: URLIntegrationParams
): string {
  const baseURLs = {
    budget: '/tools/cybersecurity-budget-calculator',
    breach_cost: '/tools/data-breach-cost-calculator',
    roi: '/tools/cybersecurity-roi-calculator'
  };

  const url = new URL(baseURLs[targetCalculator], 'https://inventivehq.com');

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      url.searchParams.set(key, value);
    }
  });

  return url.pathname + url.search;
}

// ============================================================================
// BUDGET → BREACH COST INTEGRATION
// ============================================================================

export interface BudgetToBreachParams {
  industry: IndustrySector;
  employeeCount: number;
  annualRevenue: number;
  recommendedBudget: number;
}

export function exportBudgetToBreachCost(
  industry: IndustrySector,
  employeeCount: number,
  annualRevenue: number,
  recommendedBudget: number
): string {
  const params: URLIntegrationParams = {
    source: 'budget',
    industry,
    employees: String(employeeCount),
    revenue: String(annualRevenue),
    recommendedBudget: String(Math.round(recommendedBudget))
  };

  return buildCalculatorURL('breach_cost', params);
}

export function importBudgetToBreachCost(params: URLIntegrationParams): Partial<BudgetToBreachParams> | null {
  if (params.source !== 'budget') return null;

  return {
    industry: params.industry as IndustrySector,
    employeeCount: params.employees ? parseInt(params.employees) : undefined,
    annualRevenue: params.revenue ? parseFloat(params.revenue) : undefined,
    recommendedBudget: params.recommendedBudget ? parseFloat(params.recommendedBudget) : undefined
  };
}

// ============================================================================
// BREACH COST → ROI INTEGRATION
// ============================================================================

export interface BreachToROIParams {
  industry: IndustrySector;
  employeeCount: number;
  annualRevenue: number;
  breachCost: number;
  recordsAtRisk: number;
}

export function exportBreachCostToROI(
  industry: IndustrySector,
  employeeCount: number,
  breachCost: number,
  recordsAtRisk: number
): string {
  const params: URLIntegrationParams = {
    source: 'breach_cost',
    industry,
    employees: String(employeeCount),
    breachCost: String(Math.round(breachCost)),
    recordsAtRisk: String(recordsAtRisk)
  };

  return buildCalculatorURL('roi', params);
}

export function importBreachCostToROI(params: URLIntegrationParams): Partial<BreachToROIParams> | null {
  if (params.source !== 'breach_cost') return null;

  return {
    industry: params.industry as IndustrySector,
    employeeCount: params.employees ? parseInt(params.employees) : undefined,
    breachCost: params.breachCost ? parseFloat(params.breachCost) : undefined,
    recordsAtRisk: params.recordsAtRisk ? parseInt(params.recordsAtRisk) : undefined
  };
}

// ============================================================================
// BUDGET → ROI INTEGRATION
// ============================================================================

export interface BudgetToROIParams {
  industry: IndustrySector;
  employeeCount: number;
  annualRevenue: number;
  recommendedBudget: number;
}

export function exportBudgetToROI(
  industry: IndustrySector,
  employeeCount: number,
  annualRevenue: number,
  recommendedBudget: number
): string {
  const params: URLIntegrationParams = {
    source: 'budget',
    industry,
    employees: String(employeeCount),
    revenue: String(annualRevenue),
    recommendedBudget: String(Math.round(recommendedBudget))
  };

  return buildCalculatorURL('roi', params);
}

export function importBudgetToROI(params: URLIntegrationParams): Partial<BudgetToROIParams> | null {
  if (params.source !== 'budget') return null;

  return {
    industry: params.industry as IndustrySector,
    employeeCount: params.employees ? parseInt(params.employees) : undefined,
    annualRevenue: params.revenue ? parseFloat(params.revenue) : undefined,
    recommendedBudget: params.recommendedBudget ? parseFloat(params.recommendedBudget) : undefined
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get source calculator display name
 */
export function getSourceCalculatorName(source: string): string {
  const names = {
    budget: 'Budget Calculator',
    breach_cost: 'Breach Cost Calculator',
    roi: 'ROI Calculator'
  };
  return names[source as keyof typeof names] || 'Calculator';
}

/**
 * Check if URL has integration parameters
 */
export function hasIntegrationParams(searchParams: URLSearchParams): boolean {
  return searchParams.has('source');
}

/**
 * Format currency for display
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

/**
 * Format number with commas
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}
